/**
 * useSyncStatus — subscribe to the auto-save watcher's "saving" state.
 *
 * True while an auto-save is in flight. ``useSaveNotice`` is its companion:
 * the sections the last auto-save could not persist. Pure subscriptions; no
 * fetches, no reducer coupling — the state lives in the sync engine module.
 */

import { useEffect, useState } from "react";
import { type SaveNotice, subscribeSaveNotice, subscribeSyncStatus } from "../../services/syncEngine";

export function useSyncStatus(): boolean {
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    // subscribeSyncStatus fires immediately with the current value, then on
    // every transition, and returns the unsubscribe fn.
    return subscribeSyncStatus(setSyncing);
  }, []);

  return syncing;
}

/** Sections the last auto-save skipped or failed; ``null`` when it was clean. */
export function useSaveNotice(): SaveNotice | null {
  const [notice, setNotice] = useState<SaveNotice | null>(null);
  useEffect(() => subscribeSaveNotice(setNotice), []);
  return notice;
}

const _sections = (numbers: string[]): string => [...new Set(numbers)].map((n) => `§${n}`).join(", ");

/** One sentence per reason, for the Save button's tooltip. */
export function describeSaveNotice(notice: SaveNotice): string {
  const parts: string[] = [];
  if (notice.trackChanges.length > 0) {
    parts.push(
      `${_sections(notice.trackChanges)} not saved: Track Changes is on. Accept or reject the changes (or turn it off) and it saves.`,
    );
  }
  if (notice.notFound.length > 0) {
    parts.push(`${_sections(notice.notFound)} not saved: its heading wasn't found in this document.`);
  }
  if (notice.failed.length > 0) {
    parts.push(`${_sections(notice.failed)} not saved: the server didn't accept the change. Press Save to retry.`);
  }
  if (notice.conflicted.length > 0) {
    parts.push(
      `${_sections(notice.conflicted)} not saved: someone else changed it since it was loaded. Your text is still in this document.`,
    );
  }
  return parts.join(" ");
}

export default useSyncStatus;
